import { Component, OnInit, ViewChild, AfterViewInit } from "@angular/core";
import { MatPaginator } from "@angular/material/paginator";
import { MatTableDataSource } from "@angular/material/table";
import { finalize } from "rxjs";
import { Plant } from "../../interfaces/asset-hierarchy/asset-hierarchy.models";
import { PlantTilAdmins } from "../../interfaces/technical-letters/plant-admins.interface";
import { MatSort } from "@angular/material/sort";
import { PlantAdminsService } from "../../services/technical-letters/plant-admins.service";
import * as ExcelJS from "exceljs";
import { CalAngularService } from "@cvx/cal-angular";

@Component({
    selector: "app-plant-admins",
    templateUrl: "./plant-admins.component.html",
    styleUrl: "./plant-admins.component.css",
    standalone: false
})
export class PlantAdminsComponent implements OnInit, AfterViewInit {
  dataSource = new MatTableDataSource<any>([]);
  loading = false;
  error: string | null = null;
  plants: Plant[] = [];
  totalItems = 0;
  globalSearchValue = "";
  displayedColumns: string[] = [
    "plantName",
    "plantAdminCai",
    "plantAdminEmail",
    "activeFlag",
  ];
  editRowId: any = null;
  plantNameFilter: string = "";
  caiFilter: string = "";
  emailFilter: string = "";
  isDeleteMode: boolean = false;
  expandedRowId = -1;
  prevExpandedRowId = -1;

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  selectedRow: any = null;

  constructor(
    private plantAdminsService: PlantAdminsService,
    private authService: CalAngularService
  ) { }

  ngOnInit(): void {
    this.loadPlants();
    this.loadPlantAdmins();
    this.dataSource.filterPredicate = (data: any, filter: string) => {
      let filterObj = {};
      try {
        filterObj = JSON.parse(filter);
      } catch {
        filterObj = {};
      }
      // Plant Name filter
      if (filterObj["plantName"]) {
        const plantName = (this.getPlantName(data.plantId) || "").toLowerCase();
        if (!plantName.includes(filterObj["plantName"].toLowerCase()))
          return false;
      }
      // CAI filter
      if (filterObj["plantAdminCai"]) {
        if (
          !(data.plantAdminCai || "")
            .toLowerCase()
            .includes(filterObj["plantAdminCai"].toLowerCase())
        )
          return false;
      }
      // Email filter
      if (filterObj["plantAdminEmail"]) {
        if (
          !(data.plantAdminEmail || "")
            .toLowerCase()
            .includes(filterObj["plantAdminEmail"].toLowerCase())
        )
          return false;
      }
      // Global search
      if (filterObj["global"]) {
        const searchStr = (
          this.getPlantName(data.plantId) +
          " " +
          data.plantAdminCai +
          " " +
          data.plantAdminEmail +
          " " +
          (data.activeFlag ? "active" : "inactive")
        ).toLowerCase();
        if (!searchStr.includes(filterObj["global"].toLowerCase()))
          return false;
      }
      return true;
    };
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  loadPlants(): void {
    this.plantAdminsService.getPlants().subscribe({
      next: (plants) => {
        this.plants = plants;
      },
      error: () => {
        this.error = "Failed to load plants.";
      },
    });
  }

  loadPlantAdmins(): void {
    this.loading = true;
    this.error = null;
    this.plantAdminsService
      .getPlantAdmins()
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (response) => {
          const newRow = {
            plantId: "",
            plantAdminCai: "",
            plantAdminEmail: "",
            activeFlag: true,
            isNew: true,
          };
          this.dataSource.data = [newRow, ...response];
          this.totalItems = response.length;
          setTimeout(() => {
            if (this.paginator) this.dataSource.paginator = this.paginator;
            if (this.sort) this.dataSource.sort = this.sort;
          });
        },
        error: (error) => {
          this.error = "Failed to load plant admins. Please try again.";
          console.error("Error loading plant admins:", error);
        },
      });
  }

  applyColumnFilters(): void {
    // Reset edit state and first row fields
    this.editRowId = null;
    this.selectedRow = null;
    this.isDeleteMode = false;

    const blankRow = this.dataSource.data.find(r => r.isNew);
    if (blankRow) {
      blankRow.plantId = "";
      blankRow.plantAdminCai = "";
      blankRow.plantAdminEmail = "";
      blankRow.activeFlag = true;
      this.dataSource.data = [...this.dataSource.data];
    }
    const filterObj: any = {
      plantName: this.plantNameFilter,
      plantAdminCai: this.caiFilter,
      plantAdminEmail: this.emailFilter,
      global: this.globalSearchValue,
    };
    this.dataSource.filter = JSON.stringify(filterObj);
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  onGlobalSearch(event: any): void {
    this.globalSearchValue = event.target.value?.toLowerCase() || "";
    this.applyColumnFilters();
  }

  onPlantChange(row: any, plantId: any, index: number): void {
    row.plantId = plantId;
  }

  onEditRow(row: any): void {
    const blankRow = this.dataSource.data.find(r => r.isNew);

    // Only show alert if switching from editing one row to another, but NOT when editing the blank row repeatedly
    if (
      !this.isDeleteMode &&
      this.editRowId !== null &&
      this.editRowId !== row.id &&
      !(row === blankRow && this.editRowId === null)
    ) {
      confirm("Please save or cancel the changes made to previous row.");
      return;
    }

    // Reset the first row if switching to another row
    if (row !== blankRow && blankRow) {
      blankRow.plantId = "";
      blankRow.plantAdminCai = "";
      blankRow.plantAdminEmail = "";
      blankRow.activeFlag = true;
      this.dataSource.data = [...this.dataSource.data];
    }

    this.isDeleteMode = false;
    this.editRowId = row.id;
    this.selectedRow = { ...row };
  }

  saveNewRecord(): void {
    let userInfo = this.authService.getAccount();
    if (this.selectedRow && !this.selectedRow.isNew) {
      if (!this.selectedRow.plantAdminEmail) {
        confirm("Please enter the Email");
        return;
      }

      // Validate email format if provided
      if (this.selectedRow.plantAdminEmail && !this.isValidEmail(this.selectedRow.plantAdminEmail)) {
        confirm("Please enter valid email");
        return;
      }

      const idx = this.dataSource.data.findIndex(
        (r) => r.id === this.selectedRow.id
      );
      if (idx > -1) {
        this.dataSource.data[idx] = { ...this.selectedRow };
      }
      const row = this.selectedRow;
      if (!row.plantId || !row.plantAdminEmail) {
        return;
      }
      const payload: PlantTilAdmins = {
        plantId: row.plantId,
        plantAdminCai: row.plantAdminCai,
        activeFlag: row.activeFlag,
        id: row.id,
        rowCreatedBy: userInfo.username,
        rowCreatedDate: row.rowCreatedDate || new Date(),
        rowUpdatedBy: userInfo.username,
        rowUpdatedDate: new Date(),
        plantAdminEmail: row.plantAdminEmail,
      };
      this.plantAdminsService.createEditPlantAdmins(payload).subscribe({
        next: (response) => {
          // API returns object with message property
          if (response && response.message) {
            confirm(response.message);
            this.prevExpandedRowId = -1;
            this.expandedRowId = -1;
          }
          this.loadPlantAdmins();
          this.selectedRow = null;
          this.editRowId = null;
          this.isDeleteMode = false;
        },
        error: (error) => {
          // Show any error message from API in confirm dialog
          if (error?.error?.message) {
            confirm(error.error.message);
          } else if (error?.message) {
            confirm(error.message);
          } else {
            confirm("Failed to update record.");
          }
          console.error("Error while updating plant admins:", error);
        },
      });
      return;
    }
    // New record, if no row selected
    const newRow = this.dataSource.data[0];
    if (!newRow.plantAdminEmail) {
      confirm("Please enter the Email");
      return;
    }

    // Validate email format if provided
    if (newRow.plantAdminEmail && !this.isValidEmail(newRow.plantAdminEmail)) {
      confirm("Please enter valid email");
      return;
    }

    if (!newRow.plantId || !newRow.plantAdminEmail) {
      return;
    }
    const payload: PlantTilAdmins = {
      plantId: newRow.plantId,
      plantAdminCai: newRow.plantAdminCai,
      activeFlag: newRow.activeFlag,
      id: 0,
      rowCreatedBy: userInfo.username,
      rowCreatedDate: new Date(),
      rowUpdatedBy: userInfo.username,
      rowUpdatedDate: new Date(),
      plantAdminEmail: newRow.plantAdminEmail,
    };
    this.plantAdminsService.createEditPlantAdmins(payload).subscribe({
      next: (response) => {
        // API returns object with message property
        if (response && response.message) {
          confirm(response.message);
          this.prevExpandedRowId = -1;
          this.expandedRowId = -1;
        }
        this.loadPlantAdmins();
        this.selectedRow = null;
        this.editRowId = null;
        this.isDeleteMode = false;
      },
      error: (error) => {
        // Show any error message from API in confirm dialog
        if (error?.error?.message) {
          confirm(error.error.message);
        } else if (error?.message) {
          confirm(error.message);
        } else {
          confirm("Failed to save new record.");
        }
        console.error("Error saving plant admins:", error);
      },
    });
  }

  cancelRecord(): void {
    this.editRowId = null;
    this.selectedRow = null;
    this.isDeleteMode = false;
    this.plantNameFilter = "";
    this.caiFilter = "";
    this.emailFilter = "";
    this.globalSearchValue = "";
    this.applyColumnFilters();

    const blankRow = this.dataSource.data.find(r => r.isNew);
    if (blankRow) {
      blankRow.plantId = "";
      blankRow.plantAdminCai = "";
      blankRow.plantAdminEmail = "";
      blankRow.activeFlag = true;
    }
  }

  deleteSelectedRecord(): void {
    if (!this.selectedRow || this.selectedRow.isNew) return;
    if (confirm("Are you sure you want to delete this record?")) {
      this.plantAdminsService.deletePlantAdmin(this.selectedRow.id).subscribe({
        next: () => {
          this.loadPlantAdmins();
          // Set delete mode flag to allow selecting other rows without alert
          this.isDeleteMode = true;
          this.selectedRow = null;
          this.editRowId = null;
        },
        error: (error) => {
          this.error = "Failed to delete record.";
          console.error("Error deleting plant admin:", error);
        },
      });
    }
  }

  trackByItem(index: number, item: any): any {
    return item.id || index;
  }

  getPlantName(plantId: any): string {
    const plant = this.plants.find((p) => p.id === plantId);
    return plant ? plant.name : "";
  }

  isValidEmail(email: string): boolean {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  }

  exportPlantAdminsToExcel() {
    if (this.dataSource.data.length === 0) {
      this.error = "No data available to export.";
      return;
    }
    const headerRow = [
      "ID",
      "Plant_ID",
      "NAME",
      "PLANT_ADMIN_CAI",
      "PLANT_ADMIN_EMAIL",
      "ACTIVE_FLAG",
    ];
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Plant_TIL_Admin");
    worksheet.addRow(headerRow);
    worksheet.getRow(1).font = {
      bold: true,
      size: 12,
      color: { argb: "000000" },
    };
    worksheet.getRow(1).alignment = {
      horizontal: "center",
      vertical: "middle",
    };
    this.dataSource.data.forEach((item) => {
      const plantName = this.getPlantName(item.plantId);
      const row = [
        item.id ?? 0,
        item.plantId == "" ? 0 : item.plantId,
        plantName,
        item.plantAdminCai,
        item.plantAdminEmail,
        item.activeFlag,
      ];
      worksheet.addRow(row).eachCell((cell) => {
        cell.alignment = { horizontal: "center", vertical: "middle" };
      });
    });
    worksheet.getColumn("E").eachCell(
      (cell) =>
      (cell.dataValidation = {
        type: "list",
        allowBlank: true,
        formulae: ['"TRUE,FALSE"'],
      })
    );
    worksheet.columns.forEach((column) => {
      let maxLength = 10;
      column.eachCell({ includeEmpty: true }, (cell) => {
        const cellValue = cell.value ? cell.value.toString() : "";
        maxLength = Math.max(maxLength, cellValue.length);
      });
      column.width = maxLength + 10;
    });
    worksheet.workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], { type: "application/octet-stream" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Plant_TIL_Admin.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
    });
  }

  onPlantNameFocus(index) {
    this.prevExpandedRowId = this.expandedRowId;
    this.expandedRowId = index;
    if (!this.expandedRowId && this.prevExpandedRowId > -1) {
      this.cancelRecord();
    }
  }

  onPageChange(): void {
    this.editRowId = null;
    this.selectedRow = null;
    this.expandedRowId = -1;
    const blankRow = this.dataSource.data.find(r => r.isNew);
    if (blankRow) {
      blankRow.plantId = "";
      blankRow.plantAdminCai = "";
      blankRow.plantAdminEmail = "";
      blankRow.activeFlag = true;
    }
  }
}
